import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { StatusChip } from "@/components/ui/status-chip";
import { getPreview } from "@/components/Template/RxTemplates";
import { formatDate } from "@/lib/formatDate";
import { useAuth } from "@/context/AuthContext";
import {
  getAllTemplates,
  addTemplateSection,
  updateTemplateSection,
  deleteTemplateSection,
} from "@/lib/api";
import {
  FileText, Search, Plus, Edit, Trash2,
  X,
} from "lucide-react";
import { TemplateBuilderModal } from "@/components/TemplateBuilderModal";
import { toast } from "@/components/ui/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

type TemplateType = "complaint" | "medicine" | "investigation" | "advice" | "diagnosis" | "quick_notes" | "examination";
type TemplateCategory = "personal" | "clinic";

interface Template {
  id: string;
  title: string;
  description?: string;
  type: TemplateType;
  value: any;
  category: TemplateCategory;
  usage_count: number;
  is_favorite: 0 | 1;
  is_active: 0 | 1;
  created_at: string;
  updated_at: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const TEMPLATE_TYPES: Record<TemplateType, { label: string }> = {
  complaint:     { label: "Complaints" },
  medicine:      { label: "Medicines" },
  investigation: { label: "Investigations" },
  advice:        { label: "Advice" },
  diagnosis:     { label: "Diagnosis" },
  examination:   { label: "Examination" },
  quick_notes:   { label: "Quick notes" },
};

// ─── Component ────────────────────────────────────────────────────────────────

/** `newRequest` opens the New template dialog each time it changes (the button sits in the page header) */
export default function TemplatesTab({ newRequest = 0, onChanged }: { newRequest?: number; onChanged?: () => void }) {
  const { currentUser } = useAuth();

  const [templates,    setTemplates]    = useState<Template[]>([]);
  const [activeTab,    setActiveTab]    = useState("all");
  const [searchTerm,   setSearchTerm]   = useState("");
  const [loading,      setLoading]      = useState(false);
  const [builderOpen,    setBuilderOpen]    = useState(false);
  const [builderInitial, setBuilderInitial] = useState<any>(null);
  const [builderLoading, setBuilderLoading] = useState(false);

  // ── Data ────────────────────────────────────────────────────────────────────

  const fetchTemplates = useCallback(async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    try {
      setLoading(true);
      const data = await getAllTemplates(currentUser.doctor_id, currentUser.clinic_id);
      setTemplates((data as any)?.templates || []);
    } catch {
      showNotification("error", "Failed to load templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => { if (newRequest) openCreateModal(); }, [newRequest]);

  // ── Notifications ───────────────────────────────────────────────────────────

  const showNotification = (type: "success" | "error", message: string) =>
    toast({ title: message, variant: type === "error" ? "destructive" : "default" });

  // ── Filtering ───────────────────────────────────────────────────────────────

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      if (activeTab !== "all" && t.type !== activeTab) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return t.title.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q);
      }
      return true;
    });
  }, [templates, activeTab, searchTerm]);

  const getTypeCount = (type: string) =>
      type === "all" ? templates.length : templates.filter(t => t.type === type).length;

  // ── Builder open/close ──────────────────────────────────────────────────────

  const openCreateModal = () => {
    setBuilderInitial({ title: "", description: "", type: "medicine", category: "personal", value: [] });
    setBuilderOpen(true);
  };

  const openEditModal = (template: Template) => {
    // Normalize value before passing to builder
    let value = template.value;

    if (typeof value === "string" && ["medicine", "complaint", "investigation"].includes(template.type)) {
      try { value = JSON.parse(value); } catch { value = []; }
    }

    if (["advice", "diagnosis", "quick_notes", "examination"].includes(template.type)) {
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          value = typeof parsed?.value === "string" ? parsed.value : value;
        } catch { /* keep as string */ }
      } else if (value && typeof value === "object") {
        value = typeof value.value === "string" ? value.value : "";
      } else {
        value = "";
      }
    }

    setBuilderInitial({ ...template, value });
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setBuilderInitial(null);
  };

  // ── CRUD ────────────────────────────────────────────────────────────────────

  const handleBuilderSave = async (data: any) => {
    setBuilderLoading(true);
    try {
      if (builderInitial?.id) {
        await updateTemplateSection(builderInitial.id, {
          ...data,
          doctor_id: currentUser?.doctor_id,
          clinic_id: currentUser?.clinic_id,
        });
        showNotification("success", "Template updated");
      } else {
        await addTemplateSection({
          ...data,
          doctor_id: currentUser?.doctor_id,
          clinic_id: currentUser?.clinic_id,
        });
        showNotification("success", "Template created");
      }
      closeBuilder();
      fetchTemplates();
      onChanged?.();
    } catch {
      showNotification("error", "Failed to save template");
    } finally {
      setBuilderLoading(false);
    }
  };

  const handleDelete = async (templateId: string, templateTitle: string) => {
    if (!currentUser?.doctor_id) return;
    if (!confirm(`Delete "${templateTitle}"?`)) return;
    try {
      setLoading(true);
      await deleteTemplateSection(templateId, currentUser.doctor_id);
      showNotification("success", "Template deleted");
      fetchTemplates();
      onChanged?.();
    } catch {
      showNotification("error", "Failed to delete template");
    } finally {
      setLoading(false);
    }
  };



  // ── Render ──────────────────────────────────────────────────────────────────

  const filtering = activeTab !== "all" || !!searchTerm;
  const typeOptions = [
    { value: "all", label: <>All <span className="tabular-nums text-muted-foreground">{getTypeCount("all")}</span></> },
    ...Object.entries(TEMPLATE_TYPES).map(([type, config]) => ({
      value: type,
      label: <>{config.label} <span className="tabular-nums text-muted-foreground">{getTypeCount(type)}</span></>,
    })),
  ];

  const RowActions = ({ template }: { template: Template }) => (
    <div className="flex items-center justify-end gap-0.5">
      <Button
        variant="ghost" size="sm" className="h-8 w-8 touch:h-10 touch:w-10 p-0 text-muted-foreground hover:text-foreground"
        onClick={(e) => { e.stopPropagation(); openEditModal(template); }}
        title="Edit" aria-label={`Edit ${template.title}`}
      >
        <Edit className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost" size="sm" className="h-8 w-8 touch:h-10 touch:w-10 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        onClick={(e) => { e.stopPropagation(); handleDelete(template.id, template.title); }}
        title="Delete" aria-label={`Delete ${template.title}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  return (
      <div>

        {/* One filter row: search, type */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
                aria-label="Search templates"
                placeholder="Search by name or description…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="h-8 touch:h-10 pl-8 pr-8 text-sm"
            />
            {searchTerm && (
              <button
                type="button" aria-label="Clear search" onClick={() => setSearchTerm("")}
                className="touch-target absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <Segmented aria-label="Template type" options={typeOptions} value={activeTab} onChange={setActiveTab} />
          </div>
        </div>

        {loading && !templates.length ? (
            <div className="rounded-lg border border-border bg-card overflow-hidden" aria-busy="true" aria-label="Loading templates">
              <div className="h-8 border-b border-border bg-muted/40" />
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="flex items-center h-10 px-4 border-b border-divider last:border-0">
                  <div className="h-3.5 w-full rounded bg-muted animate-pulse" />
                </div>
              ))}
            </div>
        ) : filteredTemplates.length === 0 ? (
            <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
              <FileText className="w-6 h-6 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-semibold text-foreground">{filtering ? "No matching templates" : "No templates yet"}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {filtering
                  ? "Nothing matches this search or type."
                  : "Save one from any section of the prescription pad, or create one here."}
              </p>
              {!filtering && (
                  <Button variant="outline" size="sm" onClick={openCreateModal} className="mt-4 gap-1.5">
                    <Plus className="w-4 h-4" /> New template
                  </Button>
              )}
            </div>
        ) : (
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              {/* Phones and tablets: one row per template */}
              <ul className="md:hidden divide-y divide-border">
                {filteredTemplates.map(template => {
                  const preview = getPreview(template);
                  return (
                    <li key={template.id}>
                      <div
                        role="button" tabIndex={0}
                        onClick={() => openEditModal(template)}
                        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openEditModal(template); } }}
                        className="flex items-start gap-3 pl-4 pr-2 py-2.5 active:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-foreground truncate">{template.title}</span>
                            {template.category === "clinic" && <StatusChip tone="accent">Clinic</StatusChip>}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {TEMPLATE_TYPES[template.type]?.label ?? template.type} · used <span className="tabular-nums">{template.usage_count || 0}</span>×
                          </p>
                          {preview && <p className="text-sm text-foreground/80 truncate">{preview}</p>}
                        </div>
                        <RowActions template={template} />
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Table — from md; portrait tablets drop Updated so "Fills in" has room to read */}
              <table className="hidden md:table w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-[24%]" />
                  <col className="w-[8rem]" />
                  <col />
                  <col className="w-[4.5rem]" />
                  <col className="hidden lg:table-column w-[7.5rem]" />
                  <col className="w-[5.5rem] touch:w-[6.5rem]" />
                </colgroup>
                <thead>
                  <tr className="h-8 border-b border-border bg-muted/40 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                    <th scope="col" className="pl-4 pr-3 text-left font-medium">Name</th>
                    <th scope="col" className="px-3 text-left font-medium">Type</th>
                    <th scope="col" className="px-3 text-left font-medium">Fills in</th>
                    <th scope="col" className="px-3 text-right font-medium">Used</th>
                    <th scope="col" className="hidden lg:table-cell px-3 text-left font-medium">Updated</th>
                    <th scope="col" className="pl-3 pr-4 text-right font-medium"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplates.map(template => {
                    const preview = getPreview(template);
                    return (
                      <tr
                        key={template.id}
                        tabIndex={0}
                        onClick={() => openEditModal(template)}
                        onKeyDown={e => { if (e.key === "Enter") openEditModal(template); }}
                        aria-label={`Edit ${template.title}`}
                        className="h-10 border-b border-divider last:border-0 cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/60"
                      >
                        <td className="pl-4 pr-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="truncate font-medium text-foreground" title={template.description || template.title}>{template.title}</span>
                            {template.category === "clinic" && <StatusChip tone="accent" className="flex-shrink-0">Clinic</StatusChip>}
                          </div>
                        </td>
                        <td className="px-3 text-muted-foreground whitespace-nowrap">{TEMPLATE_TYPES[template.type]?.label ?? template.type}</td>
                        <td className="px-3">
                          {preview
                            ? <span className="block truncate text-foreground/80" title={preview}>{preview}</span>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 text-right font-mono tabular-nums text-foreground">{template.usage_count || 0}</td>
                        <td className="hidden lg:table-cell px-3 text-muted-foreground tabular-nums whitespace-nowrap">{template.updated_at ? formatDate(template.updated_at) : "—"}</td>
                        <td className="pl-3 pr-2"><RowActions template={template} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        )}

        <TemplateBuilderModal
            open={builderOpen}
            onClose={closeBuilder}
            onSave={handleBuilderSave}
            initialData={builderInitial || {}}
            loading={builderLoading}
        />

      </div>
  );
}